import React, { useState, useEffect, useRef } from "react";
import { X, Save, RotateCcw, Loader2, MousePointer2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useElementOverrides } from "@/contexts/ElementOverrideContext";
import {
  CATEGORY_CONTROLS,
  STYLE_LABELS,
  ElementStyle,
  findGroupForDesignId,
} from "@/lib/elementOverrideTypes";

/**
 * Contextual panel that appears when an element is selected.
 * Shows only the relevant color controls for the element's category.
 */
export function ElementEditorPanel() {
  const {
    selectedElement,
    setSelectedElement,
    getElementStyle,
    setElementProp,
    resetElement,
    saveToCloud,
    isSaving,
    setSelectMode,
  } = useElementOverrides();

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [initialized, setInitialized] = useState(false);
  const [applyToGroup, setApplyToGroup] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Detect group membership
  const group = selectedElement ? findGroupForDesignId(selectedElement.designId) : undefined;

  // Reset group toggle when element changes
  useEffect(() => {
    setApplyToGroup(false);
  }, [selectedElement?.designId]);

  // Center on first open
  useEffect(() => {
    if (selectedElement && !initialized) {
      setPosition({
        x: Math.round(window.innerWidth / 2 - 190),
        y: Math.max(window.innerHeight - 420, 16),
      });
      setInitialized(true);
    }
    if (!selectedElement) setInitialized(false);
  }, [selectedElement, initialized]);

  // Drag handlers — only applied to the header
  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
  };
  const handlePointerUp = () => setIsDragging(false);

  if (!selectedElement) return null;

  const isDark = document.documentElement.classList.contains("dark");
  const mode = isDark ? "dark" : "light";
  const controls = CATEGORY_CONTROLS[selectedElement.category];
  const savedStyle = getElementStyle(selectedElement.designId, mode);
  // Merge: saved overrides take priority, computed colors fill in blanks
  const currentStyle = { ...selectedElement.computedColors, ...savedStyle };

  const handleChange = (prop: keyof ElementStyle, hex: string) => {
    setElementProp(selectedElement.designId, mode, prop, hex);

    // Immediately apply to DOM for instant feedback
    const applyToEl = (el: HTMLElement | undefined, style: Partial<Record<keyof ElementStyle, string>>) => {
      if (!el) return;
      if (style.backgroundColor) el.style.backgroundColor = style.backgroundColor;
      if (style.textColor) el.style.color = style.textColor;
      if (style.borderColor) { el.style.borderColor = style.borderColor; el.style.borderStyle = "solid"; }
      if (style.borderWidth) el.style.borderWidth = `${style.borderWidth}px`;
      if (style.iconColor) {
        el.querySelectorAll<SVGElement>('svg').forEach(svg => { (svg as any).style.color = style.iconColor; });
      }
    };
    applyToEl(selectedElement.element, { [prop]: hex } as any);
    if (selectedElement.actualElement) applyToEl(selectedElement.actualElement, { [prop]: hex } as any);

    // Propagate to all group members if toggled on
    if (applyToGroup && group) {
      for (const id of group.memberIds) {
        if (id !== selectedElement.designId) {
          setElementProp(id, mode, prop, hex);
        }
      }
    }
  };

  const handleReset = () => {
    resetElement(selectedElement.designId);
    if (applyToGroup && group) {
      for (const id of group.memberIds) {
        if (id !== selectedElement.designId) {
          resetElement(id);
        }
      }
    }
  };

  const handleSelectAnother = () => {
    setSelectedElement(null);
    setSelectMode(true);
  };

  return (
    <div
      ref={panelRef}
      className="fixed z-[9998] bg-card border-2 border-primary rounded-2xl shadow-2xl w-[380px] max-w-[95vw] overflow-hidden"
      style={{ left: position.x, top: position.y }}
      dir="rtl"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-primary text-primary-foreground"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <span className="font-bold text-sm truncate">🎨 {selectedElement.label}</span>
        <div className="flex gap-1" onPointerDown={(e) => e.stopPropagation()}>
          <button onClick={() => setSelectedElement(null)} className="hover:opacity-70 p-1" title="סגור">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mode indicator */}
      <div className="text-xs text-center py-1 bg-muted text-muted-foreground">
        {isDark ? "🌙 מצב כהה" : "☀️ מצב בהיר"} • {selectedElement.designId}
      </div>

      {/* Group toggle */}
      {group && (
        <button
          onClick={() => setApplyToGroup(prev => !prev)}
          className={`w-full text-xs py-1.5 px-3 flex items-center justify-center gap-1.5 transition-colors ${
            applyToGroup
              ? "bg-primary/15 text-primary font-semibold"
              : "bg-muted/50 text-muted-foreground hover:bg-muted"
          }`}
        >
          {applyToGroup ? "✅" : "📋"} {group.label} ({group.memberIds.length})
        </button>
      )}

      {/* Controls */}
      <div className="p-3 space-y-2.5">
        {controls.map(prop => {
          if (prop === "borderWidth") {
            return (
              <div key={prop} className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground truncate flex-1">{STYLE_LABELS[prop]}</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="1"
                    value={currentStyle.borderWidth || ""}
                    onChange={(e) => handleChange("borderWidth", e.target.value)}
                    className="w-[72px] rounded border border-border bg-background text-foreground px-1.5 py-1 text-[11px] font-mono"
                    placeholder="px"
                  />
                  <span className="text-[10px] text-muted-foreground">px</span>
                </div>
              </div>
            );
          }
          return (
            <ColorRow
              key={prop}
              label={STYLE_LABELS[prop]}
              value={currentStyle[prop] || ""}
              onChange={(hex) => handleChange(prop, hex)}
            />
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex gap-2 px-3 pb-3">
        <Button size="sm" variant="outline" onClick={handleReset} className="flex-1 text-xs">
          <RotateCcw className="w-3 h-3 ml-1" />
          איפוס
        </Button>
        <Button size="sm" onClick={saveToCloud} disabled={isSaving} className="flex-1 text-xs">
          {isSaving ? <Loader2 className="w-3 h-3 animate-spin ml-1" /> : <Save className="w-3 h-3 ml-1" />}
          שמור לענן
        </Button>
      </div>
    </div>
  );
}

/** Individual color control row */
function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  const [hexInput, setHexInput] = useState(value || "");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setHexInput(value || "");
    }
  }, [value, isFocused]);

  const commitHex = (val: string) => {
    const clean = val.startsWith("#") ? val : `#${val}`;
    if (/^#[0-9A-Fa-f]{6}$/.test(clean)) {
      onChange(clean);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground truncate flex-1">{label}</span>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={value || "#888888"}
          onChange={(e) => {
            onChange(e.target.value);
            setHexInput(e.target.value);
          }}
          className="w-8 h-8 rounded cursor-pointer border border-border"
          style={{ padding: 0 }}
        />
        <input
          ref={inputRef}
          type="text"
          value={hexInput}
          onFocus={() => setIsFocused(true)}
          onChange={(e) => {
            const v = e.target.value;
            setHexInput(v);
            const clean = v.startsWith("#") ? v : `#${v}`;
            if (/^#[0-9A-Fa-f]{6}$/.test(clean)) {
              onChange(clean);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            commitHex(hexInput);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") commitHex(hexInput); }}
          className="w-[72px] rounded border border-border bg-background text-foreground px-1.5 py-1 text-[11px] font-mono uppercase"
          maxLength={7}
          placeholder="לא הוגדר"
        />
      </div>
    </div>
  );
}
